import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUserNavigation from "../../useUserNavigation";
import { isTokenExpired, parseJWT } from "../../utils/auth/isTokenExpired";
import { DefaultUserState } from "./AuthorizationState";
import useAuthorizationManager from "./useAuthorizationManager";
import useUserContext from "./useUserContext";

const pingUrl = import.meta.env.VITE_APP_PING_URL;
const endSessionUrl = import.meta.env.VITE_APP_PING_END_SESSION_URL;

type AuthenticationProps = {
  children: React.ReactNode;
};

const Authentication = ({ children }: AuthenticationProps) => {
  const AuthorizationManager = useAuthorizationManager();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserContext();
  const { hasAccessToCurrentRoute, navigateToHomepage } = useUserNavigation();

  function isOnlyAriaRoles(roles: string[] = [], env: string): boolean {
    if (!roles.length) return false;
    const ariaPrefix = `deepsense_${env}_aria`;
    return roles.every((role) => role.toLowerCase().startsWith(ariaPrefix));
  }

  // Redirect user to Aria if user has only Aria roles
  useEffect(() => {
    const env = import.meta.env.VITE_APP_APP_ENV;
    const ariaUrl = import.meta.env.VITE_APP_ARIA_URL;
    if (!isTokenExpired(user.token)) {
      if (isOnlyAriaRoles(user.assignedRoles, env)) {
        window.location.replace(ariaUrl);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user.token) return;

    const decodedJWT = parseJWT(user.token);
    const expirationTime = decodedJWT ? decodedJWT.exp * 1000 : 0;
    const now = Date.now();
    const msUntilExpiry = expirationTime - now;
    if (msUntilExpiry <= 0) {
      AuthorizationManager.clearSession();
      AuthorizationManager.setUserState(DefaultUserState);
      window.location.replace(pingUrl);
    } else {
      const timer = setTimeout(() => {
        AuthorizationManager.clearSession();
        AuthorizationManager.setUserState(DefaultUserState);
        window.location.replace(pingUrl);
      }, msUntilExpiry);

      return () => clearTimeout(timer);
    }
  }, [user.token]);

  // Handle logout and role selection intents
  useEffect(() => {
    const { UserState } = AuthorizationManager;

    if (UserState.intent === "logout") {
      AuthorizationManager.clearSession();
      window.location.replace(endSessionUrl);
    }

    if (UserState.intent === "select-role") {
      AuthorizationManager.setUserState((prev) => ({
        ...prev,
        selectedRoles: [],
        isLoading: true,

        // Make sure to clear the intent
        intent: undefined,
      }));

      // Send the user to the role selection page
      navigate("/auth");
    }
  }, [AuthorizationManager.UserState.intent, AuthorizationManager, navigate]);

  // catch the case where user is not authorized to view the application
  if (location.pathname.includes("auth") && location.search.includes("error")) {
    throw new Error("You are not authorized to view this application.");
  }

  // If in auth flow, let content pass through
  if (["auth", "error", "logout"].some((path) => location.pathname.includes(path))) {
    return children;
  }

  if (!user.isLoading && !isTokenExpired(user.token)) {
    // Route the user to their target homepage if at root "/"
    if (location.pathname === "/") {
      navigateToHomepage();
    }

    // If the user is trying to access a page they don't have access to
    if (!hasAccessToCurrentRoute()) {
      console.warn(`User does not have access to: ${location.pathname}, Redirecting to homepage`);
      navigateToHomepage();
    }

    return children;
  }
  // If the user is in a fully expired state, return them to the login screen
  else if (!user.token || isTokenExpired(user.token)) {
    // Redirect immediately if token missing
    window.location.replace(pingUrl);
    return null;
  }

  return children;
};

export default Authentication;
